import { faExternalLink } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { memo, useCallback, useContext, useEffect, useState } from 'react';
import { ObjectSchema, object, string } from 'yup';

import InPlaceEditor from '@/components/PersonInPlaceEditor/components/InPlaceEditor/InPlaceEditor';
import { Field, FieldTypes } from '@/components/PersonInPlaceEditor/types';
import { MIN_CHARS_TO_SEARCH } from '@/constants/search';
import AppContext from '@/contexts/appContext';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import usePeople from '@/hooks/usePeople';
import PersonContext from '@/pages/Person/PersonContext';
import EmergencyContactRepository from '@/repositories/EmergencyContactRepository';
import { EmergencyContact } from '@/types/person';
import DataList, { DataRow } from '@/ui/components/DataList';
import Heading from '@/ui/components/Heading';
import formatPersonName from '@/utils/formatPersonName';
import mapAxiosErrorMessages from '@/utils/mapAxiosErrorMessages';

const emergencyContactSchema: ObjectSchema<Omit<EmergencyContact, 'id' | 'person_to_contact'>> =
  object().shape({
    person_id: string().required(),
    name: string().optional().nullable(),
    relationship: string().optional().nullable(),
    phone: string().optional().nullable(),
    person_to_contact_id: string().optional().nullable()
  });

const hiddenPersonId: Field<EmergencyContact> = {
  alwaysSend: true,
  key: 'person_id',
  label: '',
  type: FieldTypes.HIDDEN
};

const relationshipField: Field<EmergencyContact> = {
  key: 'relationship',
  label: 'Relationship',
  type: FieldTypes.TEXT,
  additionalCaption: { value: 'e.g. parent, sibling, spouse' }
};

const EmergencyContactSection = () => {
  const { addMessage } = useContext(AppContext);
  const { refetch: refetchPerson, person } = useContext(PersonContext);
  const { navigateToProfileWithId } = useNavigateToPage();
  const emergencyContactRepository = EmergencyContactRepository();
  const { emergency_contact } = person;

  const [isLinkedMode, setIsLinkedMode] = useState(
    !emergency_contact || !!emergency_contact.person_to_contact
  );

  // Sync mode when the emergency contact changes (e.g. after save)
  useEffect(() => {
    setIsLinkedMode(!emergency_contact || !!emergency_contact.person_to_contact);
  }, [emergency_contact]);

  const [peopleSearchParams, setPeopleSearchParams] = useState({
    my_people: false,
    page: '1',
    has_full_name: true,
    search: ''
  });

  const onPersonSearch = async (search: string) => {
    if (search.length >= MIN_CHARS_TO_SEARCH) {
      setPeopleSearchParams((current) => ({ ...current, search }));
    }
  };

  const { data: peopleData, isFetching: isFetchingPeople } = usePeople({
    params: peopleSearchParams,
    enabled: !!peopleSearchParams.search
  });

  const formatPersons = useCallback(async () => {
    if (peopleData?.data)
      return peopleData.data.results.map((p) => ({
        label: formatPersonName({ person: p }),
        value: p.id
      }));
    return [];
  }, [peopleData?.data]);

  const personSelectField: Field<EmergencyContact> = {
    key: 'person_to_contact_id',
    isLoadingOptions: isFetchingPeople,
    label: 'Linked Person',
    onSearch: onPersonSearch,
    placeholder: 'Search for an existing person',
    options: formatPersons,
    type: FieldTypes.SELECT,
    additionalCaption: {
      value: (
        <span className="flex gap-3">
          <a
            className="text-blue-600 underline"
            href="/people/new"
            target="_blank"
            rel="noreferrer">
            Create New Person
            <FontAwesomeIcon icon={faExternalLink} className="ml-1" />
          </a>
          <button
            className="text-gray-500 underline"
            onClick={() => setIsLinkedMode(false)}
            type="button">
            Enter manually instead
          </button>
        </span>
      )
    }
  };

  const nameField: Field<EmergencyContact> = {
    key: 'name',
    label: 'Name',
    type: FieldTypes.TEXT,
    additionalCaption: {
      value: (
        <button
          className="text-gray-500 underline"
          onClick={() => setIsLinkedMode(true)}
          type="button">
          Link to existing person instead
        </button>
      )
    }
  };

  const phoneField: Field<EmergencyContact> = {
    key: 'phone',
    label: 'Phone',
    type: FieldTypes.TEXT
  };

  const linkedModeFields: Field<EmergencyContact>[] = [
    hiddenPersonId,
    personSelectField,
    relationshipField
  ];

  const manualModeFields: Field<EmergencyContact>[] = [
    hiddenPersonId,
    nameField,
    phoneField,
    relationshipField
  ];

  // When saving in manual mode, explicitly clear person_to_contact_id
  const manualModeManipulator = useCallback(
    async (values: Partial<EmergencyContact>) =>
      ({ ...values, person_to_contact_id: null }) as EmergencyContact,
    []
  );

  const activeFields = isLinkedMode ? linkedModeFields : manualModeFields;

  const mutateEmergencyContact = useMutation({
    mutationFn: ({ id, values }: { id?: string; values: Partial<EmergencyContact> }) => {
      if (!id) {
        return emergencyContactRepository.createEmergencyContact(values);
      }
      return emergencyContactRepository.updateEmergencyContact(id, values);
    },
    onSuccess: async (response) => {
      if (!response) return;
      await refetchPerson();
      addMessage('success', 'Emergency contact saved.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const deleteEmergencyContact = useMutation({
    mutationFn: (id: string) => emergencyContactRepository.deleteEmergencyContact(id),
    onSuccess: async () => {
      await refetchPerson();
      addMessage('success', 'Emergency contact deleted.');
    },
    onError: (error: AxiosError) => {
      addMessage(mapAxiosErrorMessages(error));
    }
  });

  const displayPhone = emergency_contact?.phone;

  return (
    <div className="group/subsection mt-4 relative">
      <Heading
        className={`${emergency_contact ? 'mb-2' : ''}`}
        value={3}
        text="Emergency Contact"
      />
      {emergency_contact ? (
        <div className="group/subsection relative">
          <InPlaceEditor
            deleteMutation={deleteEmergencyContact}
            disableEdit={mutateEmergencyContact.isPending || deleteEmergencyContact.isPending}
            fields={activeFields}
            mutation={mutateEmergencyContact}
            record={emergency_contact}
            schema={emergencyContactSchema}
            submittedValuesManipulator={isLinkedMode ? undefined : manualModeManipulator}
            subsectionEditor>
            <DataList>
              <DataRow label="Name" sensitive>
                {emergency_contact.person_to_contact ? (
                  <button
                    className="mp-sensitive text-blue-600 underline"
                    onClick={() => navigateToProfileWithId(emergency_contact.person_to_contact!.id)}
                    type="button">
                    {formatPersonName({ person: emergency_contact.person_to_contact })}
                    <FontAwesomeIcon icon={faExternalLink} className="ml-1" />
                  </button>
                ) : (
                  (emergency_contact.name ?? 'None')
                )}
              </DataRow>
              <DataRow label="Relationship">{emergency_contact.relationship ?? 'None'}</DataRow>
              <DataRow label="Phone" sensitive>
                {displayPhone ? (
                  <a
                    className="inline-block overflow-hidden text-ellipsis text-blue-600 w-full"
                    target="_blank"
                    rel="noreferrer"
                    href={`tel:${displayPhone}`}>
                    {displayPhone}
                  </a>
                ) : (
                  'None'
                )}
              </DataRow>
            </DataList>
          </InPlaceEditor>
        </div>
      ) : (
        <div className="group/subsection relative">
          <InPlaceEditor
            fields={activeFields}
            mutation={mutateEmergencyContact}
            newRecord
            record={{ person_id: person.id }}
            schema={emergencyContactSchema}
            submittedValuesManipulator={isLinkedMode ? undefined : manualModeManipulator}
          />
        </div>
      )}
    </div>
  );
};

export default memo(EmergencyContactSection);
